import { equivalenceKey, normalizeContentText, normalizedWords, stableId } from './contentNormalizer';
import { tokenizeContent } from './contentTokenizer';
import type { CompiledPuzzle } from './contentPackSchema';
import type { AnswerAlias, EquivalenceRule, Riddle } from './types';

export class ContentCompilationError extends Error {
  constructor(public puzzleId: string, public field: string, cause: string) {
    super(`Énigme ${puzzleId} · ${field} : ${cause}`);
  }
}

function bool(value: boolean): number { return value ? 1 : 0; }

export function compilePuzzle(
  riddle: Riddle,
  aliases: AnswerAlias[],
  globalRules: EquivalenceRule[],
): CompiledPuzzle {
  if (!riddle.answer_display.trim()) throw new ContentCompilationError(riddle.id, 'answer_display', 'réponse vide');
  if (!riddle.content_text.trim()) throw new ContentCompilationError(riddle.id, 'source_text', 'texte vide');

  const answerNormalized = normalizeContentText(riddle.answer_display);
  const answerWords = normalizedWords(riddle.answer_display);
  const answerWordDisplays = tokenizeContent(riddle.answer_display).filter((token) => token.type === 'word').map((token) => token.original);
  const excluded = new Set((riddle.manual_excluded_words ?? []).map(normalizeContentText));
  const manualRules: EquivalenceRule[] = (riddle.manual_equivalence_groups ?? []).map((group, index) => ({
    id: stableId(riddle.id, 'manual_rule', index, group.group_key ?? ''),
    content_pack_id: riddle.content_pack_id,
    puzzle_id: riddle.id,
    language_code: riddle.language_code,
    scope: 'puzzle',
    group_key: group.group_key || `manual_${index}`,
    variants: group.variants,
    rule_type: group.rule_type || 'equivalent',
    sort_order: group.sort_order ?? index,
    is_active: true,
  }));
  const rules = [...globalRules.filter((rule) => rule.is_active), ...manualRules]
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  const variantGroups = new Map<string, string>();
  for (const rule of rules) for (const variant of rule.variants) variantGroups.set(normalizeContentText(variant), rule.group_key);
  const groupFor = (word: string) => variantGroups.get(word) ?? equivalenceKey(word);
  const answerGroupKeys = answerWords.map(groupFor);

  const compiledAnswerWords = answerWords.map((word, index) => {
    const isExcluded = excluded.has(word);
    return {
      id: stableId(riddle.id, 'answer_word', index), puzzle_id: riddle.id, word_index: index,
      word_display: answerWordDisplays[index] ?? word,
      word_normalized: word, equivalence_group_key: groupFor(word),
      can_be_revealed_by_player: bool(!isExcluded), can_be_revealed_by_hint: bool(!isExcluded),
    };
  });

  const tokens = tokenizeContent(riddle.content_text);
  const wordTokenIndexes = tokens.map((token, index) => ({ token, index })).filter(({ token }) => token.type === 'word');
  const excludedTokenIndexes = new Set<number>();
  for (const excludedValue of riddle.manual_excluded_words ?? []) {
    const phrase = normalizedWords(excludedValue);
    if (phrase.length === 0) continue;
    for (let start = 0; start <= wordTokenIndexes.length - phrase.length; start += 1) {
      if (phrase.every((word, offset) => wordTokenIndexes[start + offset].token.normalized === word)) {
        for (let offset = 0; offset < phrase.length; offset += 1) excludedTokenIndexes.add(wordTokenIndexes[start + offset].index);
      }
    }
  }
  const occurrences = new Map<string, { first: number; original: string; count: number; excluded: boolean; answerIndex: number | null }>();
  const compiledTokens = tokens.map((token, index) => {
    const normalized = token.normalized;
    const answerIndex = normalized ? answerGroupKeys.indexOf(groupFor(normalized)) : -1;
    const manuallyExcluded = normalized ? excluded.has(normalized) || excludedTokenIndexes.has(index) : false;
    const revealable = token.type === 'word' && answerIndex < 0 && !manuallyExcluded;
    const groupKey = normalized ? groupFor(normalized) : null;
    if (normalized && groupKey) {
      const current = occurrences.get(groupKey);
      if (current) { current.count += 1; current.excluded ||= manuallyExcluded; }
      else occurrences.set(groupKey, { first: index, original: token.original, count: 1, excluded: manuallyExcluded, answerIndex: answerIndex >= 0 ? answerIndex : null });
    }
    return {
      id: stableId(riddle.id, 'token', index), puzzle_id: riddle.id, token_index: index,
      token_type: token.type, original_text: token.original, normalized_text: normalized,
      equivalence_group_key: groupKey, word_group_key: groupKey,
      char_length: Array.from(token.original).length, is_revealable: bool(revealable),
      is_hint_eligible: bool(revealable), is_answer_word: bool(answerIndex >= 0),
      is_manually_excluded: bool(manuallyExcluded), answer_word_index: answerIndex >= 0 ? answerIndex : null,
    };
  });

  const wordGroups = [...occurrences.entries()].sort((a, b) => a[1].first - b[1].first || a[0].localeCompare(b[0])).map(([key, value]) => {
    const variants = rules.find((rule) => rule.group_key === key)?.variants ?? [value.original];
    const normalizedVariants = [...new Set(variants.map(normalizeContentText))].sort();
    const originalVariants = [...new Set(variants)].sort((a, b) => a.localeCompare(b));
    const isAnswer = value.answerIndex !== null;
    return {
      id: stableId(riddle.id, 'group', key), puzzle_id: riddle.id, group_key: key,
      display_label: value.original, normalized_variants_json: JSON.stringify(normalizedVariants),
      original_variants_json: JSON.stringify(originalVariants), first_token_index: value.first,
      occurrence_count: value.count, is_revealable: bool(!isAnswer && !value.excluded),
      is_hint_eligible: bool(!isAnswer && !value.excluded), is_answer_group: bool(isAnswer),
      is_manually_excluded: bool(value.excluded),
    };
  });

  return {
    puzzle: {
      id: riddle.id, content_pack_id: riddle.content_pack_id, category_id: riddle.category_id,
      language_code: riddle.language_code, title_internal: riddle.title_internal,
      answer_display: riddle.answer_display, answer_normalized: answerNormalized,
      source_text: riddle.content_text, difficulty_estimated: riddle.difficulty,
      is_daily_eligible: bool(riddle.is_daily_eligible ?? true), status: riddle.status,
      sort_order: typeof riddle.game_data?.sort_order === 'number' ? riddle.game_data.sort_order : null,
      revision: riddle.revision, created_at: riddle.created_at ?? null, updated_at: riddle.updated_at ?? null,
    },
    aliases: aliases.sort((a, b) => a.normalized_display.localeCompare(b.normalized_display)).map((alias, index) => ({
      id: stableId(riddle.id, 'alias', alias.normalized_display, index), puzzle_id: riddle.id,
      display: alias.display, normalized: normalizeContentText(alias.display), type: alias.alias_type || 'accepted',
      is_full_answer: bool(alias.is_full_answer ?? true),
    })),
    answerWords: compiledAnswerWords,
    tokens: compiledTokens,
    wordGroups,
    equivalenceRules: rules.map((rule) => ({
      id: rule.id || stableId(riddle.id, 'rule', rule.group_key), content_pack_id: riddle.content_pack_id,
      puzzle_id: rule.puzzle_id, language_code: rule.language_code, scope: rule.scope,
      group_key: rule.group_key, variants_json: JSON.stringify([...rule.variants].sort()),
      rule_type: rule.rule_type, sort_order: rule.sort_order,
    })),
  };
}
